/**
 * @file Entrypoint for surface page.
 */
import { useSurfaceStartingStateStore } from '@@/pinia/surface_starting_state'
import { useSurfaceUrlHashShortcuts } from '@@/vuecomposables/surface_url_hash_shortcuts'
import { configure as configureArvo } from '@padlet/arvo'
import { FetchError, fetchResponse } from '@padlet/fetch'
import { FetchOptionCredentials, FetchOptionMode, HTTPMethod } from '@padlet/fetch/lib/types'
import { MENTION_ELEMENT_TAG_NAME, PdltMention } from '@padlet/universal-post-editor'
import { setOauthToken as setJqueryAjaxOauthToken } from 'bits/ajax'
import { injectCommands } from 'bits/commands'
import { observeConnectivity } from 'bits/connectivity'
import device from 'bits/device'
import { captureException, captureMessage, isNetworkFetchError, setScope } from 'bits/error_tracker'
import { isAppUsing, isDebugMode } from 'bits/flip'
import window from 'bits/global'
import { captureOnlyWhitelistedClicks } from 'bits/pepin'
import { setTimezoneCookie } from 'bits/timezone'
import { isStringTruthy } from 'bits/truthy'
import { PageReloadableError, setupVueApp } from 'bits/vue'
import { install as installNativeBridge } from 'native_bridge/install'
import type { Pinia } from 'pinia'
import { useZoomRealtimeStore } from 'pinia/zoom_realtime'
import { trackPrinting } from 'surface/printing'
import SurfaceApp from 'vuecomponents/surface_app.vue'
import { useSurfaceCommands } from 'vuecomposables/surface_commands'
import { blankState, setupNativeBridgeXstore, storeStructure } from 'vuexstore/surface/index'
import { initialPostActionState, initialSurfaceState } from 'vuexstore/surface/initial_state'
import type { RootState } from 'vuexstore/surface/types'

setTimezoneCookie()

if (isDebugMode) {
  import('bits/perf')
    .then(({ default: trackMeasurement }) => {
      trackMeasurement()
    })
    .catch(captureException)
}

let startingStatePath
let preloadedStartingState

// Some data are too big/slow to be serialized so we don't put them in vueStartingState
// We will fetch them here. User will see the Padlet background loaded and then the content loaded after a while instead of waiting a long time with a blank page
async function loadData(): Promise<void> {
  // preloadedStartingState is preloaded (see show.html.erb), this should just get the preloaded data
  startingStatePath = document.getElementById('starting-state-preload')?.getAttribute('href')
  if (typeof startingStatePath !== 'string') {
    captureMessage('Surface startingStatePath absent')
    return
  }

  try {
    // for browsers that do not support preload, it will run a normal fetch request
    // NOTE: cors doesn't seem to work on Safari: https://stackoverflow.com/questions/52635660/can-link-rel-preload-be-made-to-work-with-fetch/63814972#63814972
    // NOTE 2: some browsers are strict about headers and will say the preload "is not used because the request headers do not match"
    //         => just use fetchResponse here instead of fetchJson with custom headers
    const preloadedStartingStateResponse = await fetchResponse(startingStatePath, {
      method: HTTPMethod.get,
      // preload send cookies with its request for resources from the same origin
      credentials: FetchOptionCredentials.include,
      // default mode is same-origin, preload uses no-cors
      mode: FetchOptionMode.noCors,
    })
    preloadedStartingState = await preloadedStartingStateResponse.json()
  } catch (error) {
    if (error instanceof FetchError) {
      const isExpiredToken = error.status === 410
      if (!isExpiredToken && !isNetworkFetchError(error)) captureException(error, { context: { startingStatePath } })
    } else {
      captureException(error, { context: { startingStatePath } })
    }

    // If the fetch fails and preloadedStartingState is absent, the page can't function. So, reload it.
    throw new PageReloadableError()
  }

  try {
    const { arvoConfig } = preloadedStartingState

    // Configure Arvo, ajax, ApiFetch
    const urlParams: URLSearchParams = new URLSearchParams(window.location.search)
    const isBeta: boolean = isStringTruthy(urlParams.get('beta')) && preloadedStartingState.user.role === 'admin'
    arvoConfig.isBeta = isBeta
    setJqueryAjaxOauthToken(arvoConfig?.token?.oauthToken)

    arvoConfig.jsonApiVersion = 6

    await configureArvo(arvoConfig)
  } catch (error) {
    captureException(error, { context: { startingStatePath } })
  }
}

let memoizedStartingState: RootState | null = null

function generateStartingState(): RootState {
  if (memoizedStartingState != null) return memoizedStartingState
  // Get blocked content providers
  const blockedContentProviders = new Set<string>(
    (preloadedStartingState.tenant?.blocked_content_providers as string[] | undefined) ?? [],
  )
  // init store
  const startingState: RootState = {
    ...blankState(),
    ...initialSurfaceState(),
    blockedContentProviders,
    postAction: preloadedStartingState.isSlideshow === true ? {} : initialPostActionState(),
    ...preloadedStartingState,
  }
  memoizedStartingState = startingState
  return startingState
}

function initializeVuexStore(store): void {
  try {
    setupNativeBridgeXstore(store)

    // Inform state when the browser goes offline/online
    observeConnectivity((isOnline: boolean): void => {
      void store.dispatch('changeConnectivity', { isOnline })
    })

    // install older native brige methods in the window object
    installNativeBridge(window, store)

    const startingState = generateStartingState()
    void store.dispatch('initializeState', startingState)
    void store.dispatch('realtime/connect')
    setScope({ userId: store.state.user.id })

    // event tracking
    captureOnlyWhitelistedClicks()
  } catch (error) {
    captureException(error, { context: { startingStatePath } })
  }
}

function initializePiniaStore(_store: Pinia): void {
  const startingState = generateStartingState()
  useSurfaceStartingStateStore().intializeState(startingState)
}

const beforeMountPromises: Array<Promise<any>> = [loadData()]
if (isAppUsing('showCommandPalette')) {
  beforeMountPromises.push(injectCommands(useSurfaceCommands().commands))
}

setupVueApp({
  el: '#app',
  usePinia: true,
  rootComponent: SurfaceApp,
  beforeMountPromises,
  storeOptions: storeStructure,
  initializeStore: initializeVuexStore,
  initializePiniaStore,
  disableAccessibilityChecks: true,
})
  .then(() => {
    // Track when user tries to print document using Ctrl+P
    trackPrinting()
    if (device.zoomApp) {
      void useZoomRealtimeStore().initializeForSurface()
    }
    window.customElements.define(MENTION_ELEMENT_TAG_NAME, PdltMention)
    useSurfaceUrlHashShortcuts()
  })
  .catch((e) => captureException(e))
